import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Map,
  DollarSign,
  Languages,
  CloudSun,
  CalendarDays,
} from "lucide-react";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";

/* Editable placeholders — each section maps to its primary affiliate destination.
 * For now these route to the Travel Tools page with the right category filter
 * (where the real affiliate URLs live). You can replace `href` with a direct
 * deeplink (e.g., your Travelpayouts URL) later. */
const SECTIONS = [
  {
    kicker: "Where to rest",
    title: "Finding Places to Stay",
    copy: "Whether staying for a few nights or settling in for longer journeys, location and comfort can shape the experience.",
    cta: "Explore Stays",
    href: "/travel-tools?category=hotels",
    image:
      "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=1400&q=70",
  },
  {
    kicker: "On the move",
    title: "Flights & Getting There",
    copy: "Finding the right route and timing often makes all the difference.",
    cta: "Find Flights",
    href: "/travel-tools?category=airlines",
    image:
      "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1400&q=70",
  },
  {
    kicker: "Beyond the guidebook",
    title: "Experiences & Local Discoveries",
    copy: "Some of the best memories come from experiences rather than destinations.",
    cta: "Explore Experiences",
    href: "/travel-tools?category=cruises",
    image:
      "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1400&q=70",
  },
  {
    kicker: "The well-packed bag",
    title: "Travel Essentials",
    copy: "Items that make travel simpler, lighter, and more comfortable.",
    cta: "Travel Essentials",
    href: "#",
    image:
      "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1400&q=70",
  },
];

const TOOLS = [
  {
    title: "Currency converter",
    note: "Live exchange rates",
    href: "https://www.xe.com/currencyconverter/",
    Icon: DollarSign,
  },
  {
    title: "Maps",
    note: "Find your way anywhere",
    href: "https://www.google.com/maps",
    Icon: Map,
  },
  {
    title: "Translation tools",
    note: "Speak the local tongue",
    href: "https://translate.google.com/",
    Icon: Languages,
  },
  {
    title: "Weather tools",
    note: "Plan around the sky",
    href: "https://www.accuweather.com/",
    Icon: CloudSun,
  },
  {
    title: "Travel planning tools",
    note: "Itineraries & wishlists",
    href: "https://wanderlog.com/",
    Icon: CalendarDays,
  },
];

export default function TravelResources() {
  return (
    <main data-testid="travel-resources-page" className="bg-[#FDFBF7]">
      <SEO
        title="Travel Resources — Globezing"
        description="Thoughtfully chosen travel tools, stays, and resources that help make journeys easier and more memorable."
        path="/travel-resources"
      />

      {/* Hero */}
      <section className="pt-36 pb-14 lg:pt-44 lg:pb-20 border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <p className="kicker">Travel Resources</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.98] mt-5">
            Thoughtfully chosen, <em className="italic">gently shared.</em>
          </h1>
          <p className="mt-8 text-stone-600 text-lg leading-relaxed max-w-2xl">
            Thoughtfully chosen travel tools, stays, and resources that help make journeys easier and more memorable.
          </p>
          <div className="mt-8">
            <ShareButtons title="Travel Resources — Globezing" description="The tools, stays, and resources we return to on every journey." />
          </div>
        </div>
      </section>

      {/* Four feature sections (alternating image side) */}
      <section className="py-20 lg:py-28 space-y-24 lg:space-y-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {SECTIONS.map((s, i) => (
            <FeatureBlock key={s.title} item={s} reversed={i % 2 === 1} index={i} />
          ))}
        </div>
      </section>

      {/* Favorite Travel Tools (icon cards) */}
      <section className="py-20 lg:py-28 bg-stone-50 border-y border-stone-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="kicker">Toolkit</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight mt-4">
              Favorite Travel Tools
            </h2>
            <p className="mt-5 text-stone-600 max-w-xl mx-auto">
              The small, dependable tools we open before every trip — and often during.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {TOOLS.map((t, i) => (
              <motion.a
                key={t.title}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`tool-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group bg-white border border-stone-200 hover:border-stone-400 transition-all p-6 flex flex-col items-center text-center"
              >
                <span className="w-12 h-12 rounded-full bg-stone-100 group-hover:bg-[#9c4122]/10 flex items-center justify-center transition-colors">
                  <t.Icon size={20} className="text-stone-700 group-hover:text-[#9c4122] transition-colors" strokeWidth={1.5} />
                </span>
                <h3 className="font-serif text-lg mt-4 text-stone-900 group-hover:text-[#9c4122] transition-colors">
                  {t.title}
                </h3>
                <p className="text-[12px] text-stone-500 mt-1.5 leading-snug">{t.note}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Sign-off + disclosure */}
      <section className="py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <img
            src="/globezing-logo-256.png"
            srcSet="/globezing-logo-256.png 1x, /globezing-logo-512.png 2x"
            alt="Globezing"
            width="64"
            height="64"
            loading="lazy"
            className="w-14 h-14 mx-auto rounded-full object-contain opacity-90"
          />
          <p className="font-serif italic text-stone-700 text-base sm:text-lg mt-5 tracking-wide">
            Think Global · Live Local
          </p>
          <p className="text-[11px] tracking-[0.35em] uppercase text-[#9c4122] font-medium mt-2">
            Postpone Nothing
          </p>

          <p
            data-testid="affiliate-disclosure"
            className="mt-14 text-xs text-stone-500 leading-relaxed max-w-2xl mx-auto italic"
          >
            Some resources on this page may contain affiliate links. If you choose to book or purchase through them, Globezing may earn a small commission at no extra cost to you.
          </p>
        </div>
      </section>
    </main>
  );
}

function FeatureBlock({ item, reversed, index }) {
  const isExternal = item.href.startsWith("http");
  const ButtonTag = isExternal ? "a" : Link;
  const buttonProps = isExternal
    ? { href: item.href, target: "_blank", rel: "noopener nofollow sponsored" }
    : { to: item.href };

  return (
    <div
      data-testid={`resource-section-${item.title.toLowerCase().replace(/&/g, "and").replace(/\s+/g, "-")}`}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
        reversed ? "lg:[&>div:first-child]:order-2" : ""
      }`}
    >
      <motion.div
        initial={{ opacity: 0, scale: 1.02 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1 }}
        className="lg:col-span-7"
      >
        <div className="aspect-[4/3] overflow-hidden bg-stone-200">
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="lg:col-span-5"
      >
        <p className="kicker text-[#9c4122]">{String(index + 1).padStart(2, "0")} · {item.kicker}</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight mt-4 text-stone-900">
          {item.title}
        </h2>
        <p className="mt-6 text-stone-700 text-[16px] sm:text-[17px] leading-relaxed">
          {item.copy}
        </p>
        <div className="mt-8">
          <ButtonTag
            {...buttonProps}
            data-testid={`resource-cta-${item.cta.toLowerCase().replace(/\s+/g, "-")}`}
            className="inline-flex items-center gap-2 px-7 py-3.5 border border-stone-900 text-stone-900 text-[12px] tracking-[0.25em] uppercase font-semibold hover:bg-stone-900 hover:text-[#FDFBF7] transition-colors"
          >
            {item.cta}
            <ArrowUpRight size={14} />
          </ButtonTag>
        </div>
      </motion.div>
    </div>
  );
}
